import TripleLayout from "../mySandpack/TripleLayout";
import { useContext, useEffect, useState } from "react";
import {
  ActivePracticeContext,
  PracticesContext,
} from "../../contexts/contextExporter";

import PracticeTitleContent from "../../components/PracticeTitleContent";
import AiContent from "./AiAnswer";
import { RevealAnswerButton, RevealedAnswerCode } from "./RevealAnswer";
import { IPracticeHistoryItem } from "../../types/generalTypes";
import PracticeHistoryNav from "./PracticeHistoryNav";

import { IPracticeCode } from "../../types/practiceTypes";
import MonacoEditor from "../../components/MonacoEditor";
import { CircleNotch } from "@phosphor-icons/react";
import PythonOutput from "./PythonOutput";

export default function CodePracticePython({
  currentAiContent,
  setAiReply,
  setUserAnswer,
  history,
  isLive,
}: {
  currentAiContent: string | null;
  setAiReply: React.Dispatch<React.SetStateAction<string | null>>;
  setUserAnswer: React.Dispatch<React.SetStateAction<string | null>>;
  history: IPracticeHistoryItem[];
  isLive: boolean;
}) {
  const {
    practice,
    sendAnswer,
    actionStatus,
    setActionStatus,
    isAnswerRevealed,
  } = useContext(ActivePracticeContext);
  const { nextPractice } = useContext(PracticesContext);

  const codePracticeBasic = practice as IPracticeCode;

  const initialValue = codePracticeBasic.description.preText || "";

  const [ans, setAns] = useState(initialValue);

  const isPastItem = actionStatus.historyIndex > -1;
  const currentHistoryItem = history[actionStatus.historyIndex];

  useEffect(() => {
    setAns(
      actionStatus.historyIndex > -1
        ? currentHistoryItem?.user_answer
        : initialValue
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionStatus.historyIndex]);

  const tryAgain = () => {
    setActionStatus((prev) => ({ ...prev, ai: "IDLE", historyIndex: -1 }));
    setAiReply(null);
  };

  const onSubmit = () => {
    setUserAnswer(ans);
    sendAnswer({ answer: ans });
  };

  return (
    <TripleLayout
      header={
        <PracticeHistoryNav
          key={practice!.id}
          current={actionStatus.historyIndex}
          total={history.length}
          setCurrent={(newHistoryIndex) =>
            setActionStatus((prev) => ({
              ...prev,
              historyIndex: newHistoryIndex,
            }))
          }
        />
      }
      left={
        <div className="text-base leading-normal h-full overflow-auto relative group">
          <div className="py-6 px-5 mb-2">
            <div className="practiceTitle font-medium p-6 text-white">
              <PracticeTitleContent title={practice!.description.title} />
            </div>

            {isAnswerRevealed && (
              <RevealedAnswerCode code={codePracticeBasic?.description?.answer || ""} />
            )}

            <AiContent
              isFocused={isPastItem || actionStatus.ai !== "IDLE"}
              aiReply={currentAiContent}
              setAiReply={setAiReply}
            />
          </div>
          <span className="text-sm text-white absolute bottom-3 right-3 transition-opacity opacity-30 group-hover:opacity-75">
            #{practice!.id}
          </span>
        </div>
      }
      right={[
        <div className="monacoWrapper">
          <MonacoEditor
            cx={isPastItem ? "" : "animate-fadeIn animate-faster"}
            language="python"
            value={ans}
            handleChange={setAns}
            key={codePracticeBasic.id}
            options={{
              readOnly: isPastItem || actionStatus.ai !== "IDLE",
            }}
          />
        </div>,
        <PythonOutput
          extraLibs={
            practice?.tags ? (practice?.tags as string).split(",").map(s => s.trim()) : []
          }
          code={ans}
        />,
      ]}
      footer={
        <div className="flex gap-2">
          {isPastItem && !actionStatus.canGoNext && !isAnswerRevealed && (
            <button className="practice-button" onClick={tryAgain}>
              Tekrar dene
            </button>
          )}

          {isPastItem && actionStatus.canGoNext && (
            <button
              className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen"
              onClick={nextPractice}
            >
              Sonraki Soru
            </button>
          )}

          {!isPastItem && actionStatus.ai === "LOADING" && (
            <button className="practice-button opacity-60 cursor-wait" disabled>
              <CircleNotch size={16} className="animate-spin inline mr-1.5" />
              Değerlendiriliyor...
            </button>
          )}
          {!isPastItem && actionStatus.ai === "IDLE" && !isAnswerRevealed && (
            <button
              type="button"
              onClick={onSubmit}
              className="practice-button"
            >
              Gönder
            </button>
          )}

          <RevealAnswerButton tryAgain={tryAgain} isLive={isLive} />
        </div>
      }
    />
  );
}
